
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { ReviewFormData } from '../review-form/useReviewForm';
import { sanitizeInput } from '@/utils/authSecurity';

export const useSubmitReview = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitToSupabase = async (formData: ReviewFormData, userId?: string) => {
    try {
      // Sanitize all text inputs for security
      const sanitizedData = {
        company: sanitizeInput(formData.company),
        job_title: sanitizeInput(formData.jobTitle),
        reason_for_exit: sanitizeInput(formData.reasonForExit),
        location: sanitizeInput(formData.location),
        job_category: formData.jobCategory ? sanitizeInput(formData.jobCategory) : null,
        tenure: formData.tenure ? sanitizeInput(formData.tenure) : null,
        exit_date: formData.exitDate ? formData.exitDate.toISOString() : null,
        fair_work_involved: formData.fairWorkInvolved,
        positive_experience: formData.positiveExperience,
        user_id: userId || null,
        salary: formData.salary || null,
      };

      // Validate required fields after sanitization
      if (!sanitizedData.company || !sanitizedData.job_title || !sanitizedData.reason_for_exit || !sanitizedData.location) {
        throw new Error('All required fields must be filled out');
      }

      // Insert the review
      const { data: reviewResult, error: reviewError } = await supabase
        .from('reviews')
        .insert(sanitizedData)
        .select('id')
        .single();

      if (reviewError) {
        console.error('Error submitting review:', reviewError);
        throw reviewError;
      }

      // If we have perk ratings, let's save them too
      if (formData.perkRatings && formData.perkRatings.length > 0) {
        // First, find the company ID based on the company name
        const { data: companyData, error: companyError } = await supabase
          .from('companies')
          .select('id')
          .eq('name', sanitizedData.company)
          .maybeSingle();

        if (companyError) {
          console.error('Error finding company:', companyError);
          // Don't throw here, we still want to save the review even if perk ratings fail
        }

        if (companyData?.id) {
          // Prepare perk ratings data with sanitization
          const perkRatingsData = formData.perkRatings.map(rating => ({
            review_id: reviewResult.id,
            company_id: companyData.id,
            perk_id: sanitizeInput(rating.perkId),
            perk_text: sanitizeInput(rating.perkText),
            is_positive: rating.isPositive,
            comment: rating.comment ? sanitizeInput(rating.comment) : null,
          }));

          // Insert perk ratings
          const { error: perkRatingsError } = await supabase
            .from('perk_ratings')
            .insert(perkRatingsData);

          if (perkRatingsError) {
            console.error('Error submitting perk ratings:', perkRatingsError);
            // Don't throw, the main review was saved successfully
          }
        }
      }

      return reviewResult?.id;
    } catch (error) {
      console.error('Error in submitToSupabase:', error);
      throw error;
    }
  };

  const handleSubmitReview = async (formData: ReviewFormData) => {
    setIsSubmitting(true);
    try {
      // Get current user if available
      const { data: { user } } = await supabase.auth.getUser();
      const userId = user?.id;
      
      // Submit review to Supabase
      const reviewId = await submitToSupabase(formData, userId);
      return reviewId;
    } catch (error) {
      console.error('Error submitting review:', error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  return { submitToSupabase, handleSubmitReview, isSubmitting };
};

export default useSubmitReview;
